/**
 * Permission Utilities - check access using backend permission codes.
 *
 * The frontend no longer ships its own permissions matrix. Backend-provided
 * effective permission keys are the source of truth. Legacy role fallback is
 * intentionally conservative and only grants implicit access to elevated roles.
 */

import type { UserRole } from "@/auth/authSlice";

export type UserTierLevel = "basic" | "standard" | "premium";

function hasImplicitAdminAccess(role: UserRole): boolean {
  return role === "admin" || role === "super_admin";
}

/**
 * Determine user tier based on role.
 * Retained for compatibility with older consumers that still read user tier.
 */
export function getRoleTier(role: UserRole): UserTierLevel {
  switch (role) {
    case "super_admin":
    case "admin":
      return "premium";
    case "staff":
    case "teacher":
      return "standard";
    case "student":
    case "parent":
      return "basic";
    default:
      return "basic";
  }
}

/**
 * Check if a permission code is in the user's permission list
 * @param permissionCode - The permission code to check (e.g., "STUDENTS_VIEW", "USERS_DELETE")
 * @param userPermissionCodes - Array of permission codes loaded from backend
 * @returns true if user has the permission
 */
export function hasPermissionCode(
  permissionCode: string,
  userPermissionCodes: string[],
): boolean {
  if (!permissionCode || !Array.isArray(userPermissionCodes)) {
    return false;
  }

  // Admin has all permissions by default
  if (userPermissionCodes.includes("ADMIN_ALL")) {
    return true;
  }

  return userPermissionCodes.includes(permissionCode);
}

/**
 * Legacy fallback for dotted permission identifiers.
 *
 * Since the frontend permissions matrix was removed, only elevated roles get
 * implicit access here. All other users must rely on backend permission codes.
 *
 * @param module - e.g., "Academics"
 * @param feature - e.g., "Students"
 * @param action - e.g., "view"
 * @param userRole - User's role
 * @returns true if user's tier is in the allowed tiers for this permission
 */
export function hasPermissionByRole(
  module: string,
  feature: string,
  action: string,
  userRole: UserRole,
): boolean {
  void module;
  void feature;
  void action;

  return hasImplicitAdminAccess(userRole);
}

/**
 * Quick helper to check just module.feature level (defaults to "view" action)
 * Uses legacy role-based permissions
 */
export function canAccessFeatureByRole(
  module: string,
  feature: string,
  userRole: UserRole,
): boolean {
  return hasPermissionByRole(module, feature, "view", userRole);
}

/**
 * Hybrid permission checker - tries backend permission codes first, then uses
 * the conservative admin-only role fallback for dotted legacy identifiers.
 * @param input - Either a permission code (string) or module.feature.action format
 * @param userRole - User's role
 * @param userPermissionCodes - Array of permission codes from backend
 * @returns true if user has the permission
 */
export function canAccess(
  input: string,
  userRole: UserRole,
  userPermissionCodes: string[],
): boolean {
  if (!input) {
    return true;
  }

  // Check if it's a backend permission code (uppercase, no dots)
  if (input === input.toUpperCase() && !input.includes(".")) {
    // Backend permission code format
    return hasPermissionCode(input, userPermissionCodes);
  }

  // Parse as legacy format: "Module.Feature.Action"
  const parts = input.split(".");
  if (parts.length === 3) {
    const [module, feature, action] = parts;
    return hasPermissionByRole(module, feature, action, userRole);
  }

  if (parts.length === 2) {
    const [module, feature] = parts;
    return canAccessFeatureByRole(module, feature, userRole);
  }

  // If parsing fails, return false
  return false;
}

/**
 * Get all permission codes that match a prefix
 * Useful for checking whether a user has at least one permission in a category
 * @param prefix - Permission prefix to match (e.g., "STUDENTS_", "PERMISSIONS_")
 * @param userPermissionCodes - Array of permission codes from backend
 * @returns Array of matching permission codes
 */
export function getMatchingPermissions(
  prefix: string,
  userPermissionCodes: string[],
): string[] {
  return userPermissionCodes.filter((code) => code.startsWith(prefix));
}

/**
 * Check whether user has at least one of the provided permission codes
 * @param permissionCodes - Array of permission codes to check
 * @param userPermissionCodes - User's permission codes from backend
 * @returns true if user has at least one of the provided permissions
 */
export function hasAnyPermission(
  permissionCodes: string[],
  userPermissionCodes: string[],
): boolean {
  return permissionCodes.some((code) => hasPermissionCode(code, userPermissionCodes));
}

/**
 * Check if user has ALL of the provided permission codes
 * @param permissionCodes - Array of permission codes to check
 * @param userPermissionCodes - User's permission codes from backend
 * @returns true if user has all of the provided permissions
 */
export function hasAllPermissions(
  permissionCodes: string[],
  userPermissionCodes: string[],
): boolean {
  return permissionCodes.every((code) => hasPermissionCode(code, userPermissionCodes));
}
